import React, { useState, useContext, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { CartContext } from "../cart/cart";
import {
  getAuthorById,
  updateAuthor,
  getAuthorByToken,
  uploadProduct,
  deleteProduct,
  BASE_URL,
} from "../utils/utils" ;
import Navbar from "../navbar/navbar";
import { AuthContext } from "../authmanager/authcontext";
import ProductDescription from "../validators/descriptor";

const Author = ({ toggleTheme }) => {
  const {
    cartItems,
    addToCart,
    removeProductByQuantityFromCart,
    finalizeCart,
  } = useContext(CartContext);
  const { isLoggedIn, handleLogout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [author, setAuthor] = useState(null); // Initialize with null
  const { id } = useParams();
  const accessToken = localStorage.getItem("accessToken");

  useEffect(() => {
    const fetchAuthorData = async () => {
      try {
        setIsLoading(true);
        if (accessToken) {
          const authorData = await getAuthorById(accessToken, parseInt(id));

          setAuthor(authorData); // Set author object
          console.log("author", authorData);
        }
      } catch (error) {
        console.error("Failed to fetch author.", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuthorData();
  }, [accessToken, id]); // Depend on accessToken and id

  const handleRemoveFromCart = (product, quantity) => {
    removeProductByQuantityFromCart(product, quantity);
  };

  return (
    <div>
      {isLoading ? (
        <p>Loading...</p>
      ) : author ? (
        <div>
          <Navbar
            toggleTheme={toggleTheme}
            handleLogout={handleLogout}
            finalizeCart={finalizeCart}
          />
          <br></br>

          <div key={author.id}>
            <div className="author-container">
              {author.image && (
                <img
                  src={author.image}
                  alt={author.name}
                  className="author-image"
                />
              )}
              <h2 className="author-name">{author.user_name}</h2>
              {author.description ? (
                <ProductDescription
                  description={author.longDescription}
                  className="author-description"
                />
              ) : (
                <p className="author-description">
                  No description available for this author.
                </p>
              )}
            </div>
            <div className="product-grid">
              {author.products.map((product) => {
                const selectedProduct = cartItems.find(
                  (item) => item.id === product.id
                );
                const quantity = selectedProduct ? selectedProduct.quantity : 0;

                return (
                  <div key={product.id} className="product-item">
                    <h3>{product.name}</h3>
                    <Link to={`/products/${product.id}`}>
                      <img
                        src={product.image}
                        alt={product.name}
                        className="product-image"
                      />
                    </Link>
                    <ProductDescription description={product.description} />
                    <p>Price: {product.price}</p>
                    <h3>{product.category.name}</h3>
                    {product.pdf?.file && (
                      <a href={product.pdf.file} download>
                        <div className="button-container">
                          <div className="circle">Download PDF</div>
                        </div>
                      </a>
                    )}
                    <div className="button-container">
                      <div className="circle">
                        <span>{`Selected: ${quantity}`}</span>
                      </div>
                      <div className="button-container">
                        <button
                          className="circle"
                          onClick={() => addToCart(product, 1)}
                        >
                          Add to Cart
                        </button>
                        {cartItems.find((item) => item.id === product.id) && (
                          <button
                            className="circle"
                            onClick={() => handleRemoveFromCart(product, 1)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <p>No author found.</p>
      )}
    </div>
  );
};
const AuthorForm = ({ accessToken }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    longDescription: "",
    image: null,
  });

  const [authorId, setAuthorId] = useState(null); // Set authorId as soon as component is called
  const [loading, setLoading] = useState(false);
  const [loadingAuthor, setLoadingAuthor] = useState(true); // State to track loading author data
  const [message, setMessage] = useState("");

  // Fetch author data by token when the component mounts or when accessToken changes
  useEffect(() => {
    if (accessToken) {
      const fetchAuthorData = async () => {
        try {
          setLoadingAuthor(true); // Set loading state to true while fetching
          const author = await getAuthorByToken(accessToken); // Fetch author by token
          console.log("Setting author data");
          setAuthorId(author.id); // Set authorId as soon as the component is called
          setFormData({
            name: author.name,
            description: author.description,
            longDescription: author.longDescription,
            image: null, // Reset image if you don't want to prefill it
          });
        } catch (error) {
          setMessage("Error fetching author data.");
        } finally {
          setLoadingAuthor(false); // Set loading state to false when done
        }
      };

      fetchAuthorData();
    }
  }, [accessToken]); // Re-fetch if accessToken changes

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle file change
  const handleFileChange = (e) => {
    setFormData({ ...formData, image: e.target.files[0] });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!authorId) {
      setMessage("Author ID is not available.");
      return;
    }

    const uploadData = new FormData();
    uploadData.append("name", formData.name);
    uploadData.append("description", formData.description);
    uploadData.append("longDescription", formData.longDescription);
    if (formData.image) {
      uploadData.append("image", formData.image);
    }

    try {
      setLoading(true);
      // Assuming `updateAuthor` function exists and handles the author update
      const response = await updateAuthor(accessToken, authorId, uploadData); // Use the authorId here
      setMessage("Author updated successfully!");
      console.log("Response:", response);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loadingAuthor) {
    return <p>Loading author data...</p>; // Show loading message while author data is being fetched
  }

  return (
    <div>
      <h2>Update Author</h2>
      <form onSubmit={handleSubmit}>
        <div>
          <label>Name:</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label>Description:</label>
          <input
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label>Long Description:</label>
          <textarea
            name="longDescription"
            value={formData.longDescription}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label>Image:</label>
          <input type="file" onChange={handleFileChange} />
        </div>
        <button type="submit" disabled={loading}>
          {loading ? "Submitting..." : "Submit"}
        </button>
      </form>
      {message && <p>{message}</p>}
    </div>
  );
};
const AuthorProductUploadForm = ({ accessToken, categories }) => {
  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
    longDescription: "",
    price: "",
    category: "", // Assuming category is an ID
    pdf: null,
    image: null,
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [authorData, setAuthorData] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormValues({ ...formValues, [name]: files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("name", formValues.name);
    formData.append("description", formValues.description);
    formData.append("longDescription", formValues.longDescription);
    formData.append("price", formValues.price);
    formData.append("category", formValues.category);
    if (formValues.pdf) {
      formData.append("pdf", formValues.pdf);
    }
    if (formValues.image) {
      formData.append("image", formValues.image);
    }

    try {
      setLoading(true);
      const response = await uploadProduct(accessToken, formData);
      setMessage("Product uploaded successfully!");
      console.log("Response:", response);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
    fetchAuthorData();
  };
  // Function to handle product deletion
  const handleDelete = async (productId) => {
    try {
      await deleteProduct(accessToken, productId); // Call the delete API
      setAuthorData((prevData) => {
        const updatedProducts = prevData.products.filter(
          (product) => product.id !== productId
        );
        return { ...prevData, products: updatedProducts };
      }); // Remove deleted product from state

      alert("Product deleted successfully!");
      fetchAuthorData();
    } catch (error) {
      console.error("Error deleting product:", error.message);
      alert("Failed to delete product");
    }
  };
  const fetchAuthorData = async () => {
    try {
      const data = await getAuthorByToken(accessToken);
      setAuthorData(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  // Fetch author data on component mount
  useEffect(() => {
    fetchAuthorData();
  }, [accessToken]);
  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!authorData) {
    return <div>No author data available</div>;
  }
  return (
    <div>
      <div>
        <h2>Product Details</h2>
        {authorData.products.length > 0 ? (
          <div className="product-grid">
            {authorData.products.map((product) => (
              <div key={product.id} className="product-item">
                <h3>{product.name}</h3>

                <div>
                  <strong>Category:</strong> {product.category.name}
                </div>

                <div>
                  <strong>Description:</strong> {product.description}
                </div>

                <div>
                  <strong>Long Description:</strong> {product.longDescription}
                </div>

                <div>
                  <strong>Price:</strong> ₹{product.price}
                </div>

                {product.image && (
                  <img
                    src={BASE_URL + product.image}
                    alt={product.name}
                    className="product-image"
                  />
                )}
                {/* Delete button */}
                <button onClick={() => handleDelete(product.id)}>Delete</button>
              </div>
            ))}
          </div>
        ) : (
          <p>No products available.</p>
        )}
      </div>
<br></br>
      <h2>Upload Product</h2>
      <form onSubmit={handleSubmit}>
        <div>
          <label>Name:</label>
          <input
            type="text"
            name="name"
            value={formValues.name}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label>Description:</label>
          <input
            type="text"
            name="description"
            value={formValues.description}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label>Long Description:</label>
          <textarea
            name="longDescription"
            value={formValues.longDescription}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label>Price:</label>
          <input
            type="number"
            name="price"
            value={formValues.price}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label>Category:</label>
          <select
            name="category"
            value={formValues.category}
            onChange={handleChange}
            required
          >
            <option value="">Select a category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id.toString()}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>PDF:</label>
          <input type="file" name="pdf" onChange={handleFileChange} />
        </div>
        <div>
          <label>Image:</label>
          <input type="file" name="image" onChange={handleFileChange} />
        </div>
        <button type="submit" disabled={loading}>
          {loading ? "Uploading..." : "Upload"}
        </button>
      </form>
      {message && <p>{message}</p>}
      <br></br>
    </div>
    
  );
};

export default Author;
export { AuthorForm, AuthorProductUploadForm };
